/**
 * Sessions Management Hook
 * Fetch and manage user sessions from the backend
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getCsrfToken } from '@/lib/auth/csrfToken';
import { API_CONFIG } from '@/lib/config/api.config';
import { logger } from '@/lib/utils/logger';
import { formatSGT } from '@/lib/utils/date';

export interface Session {
  id: string;
  user_id: string;
  device_fingerprint: string;
  device_name?: string;
  device_type?: string;
  browser?: string;
  os?: string;
  ip_address: string;
  country?: string;
  city?: string;
  created_at: string;
  last_activity: string;
  expires_at: string;
  initial_auth_time: string;
  last_token_refresh: string;
  refresh_token_created: string;
  rolling_refresh_count: number;
  user_agent?: string;
}

interface DeviceInfo {
  device_fingerprint: string;
  device_name?: string;
  device_type?: string;
  browser?: string;
  os?: string;
  last_seen?: string;
}

export interface SessionsResponse {
  sessions: Session[];
  devices: DeviceInfo[];
  current_session_id: string;
}

/**
 * Fetch all active sessions for the current user
 */
export function useSessions() {
  return useQuery({
    queryKey: ['auth', 'sessions'],
    queryFn: async () => {
      try {
        logger.info('useSessions', 'Fetching user sessions');
        const response = await fetch(`${API_CONFIG.BASE_URL}/v1/auth/sessions`, {
          method: 'GET',
          credentials: 'include', // Include cookies
        });

        if (!response.ok) {
          logger.error('useSessions', `HTTP ${response.status}: ${response.statusText}`);
          throw new Error('Failed to fetch sessions');
        }

        const data = await response.json();
        logger.info('useSessions', `Fetched ${data.sessions?.length || 0} sessions`);
        return data as SessionsResponse;
      } catch (error) {
        logger.error('useSessions', error);
        throw error;
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
    retry: 1,
  });
}

/**
 * Revoke a specific session
 */
export function useRevokeSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sessionId: string) => {
      try {
        logger.info('useRevokeSession', `Revoking session ${sessionId}`);
        // DELETE is CSRF-protected server-side; attach the token like other mutations
        const headers: Record<string, string> = {};
        const csrfToken = getCsrfToken();
        if (csrfToken) {
          headers['X-CSRF-Token'] = csrfToken;
        }
        const response = await fetch(`${API_CONFIG.BASE_URL}/v1/auth/sessions/${sessionId}`, {
          method: 'DELETE',
          credentials: 'include',
          headers,
        });

        if (!response.ok) {
          logger.error('useRevokeSession', `HTTP ${response.status}: ${response.statusText}`);
          throw new Error('Failed to revoke session');
        }

        logger.info('useRevokeSession', `Session ${sessionId} revoked successfully`);
        return await response.json();
      } catch (error) {
        logger.error('useRevokeSession', error);
        throw error;
      }
    },
    onSuccess: () => {
      // Invalidate sessions query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['auth', 'sessions'] });
    },
  });
}

/**
 * Format session info for display
 */
export function formatSessionInfo(session: Session) {
  const deviceName =
    session.device_name || session.device_type || session.browser || 'Unknown Device';

  // Location is best-effort (CDN geo headers); most environments don't
  // provide it, so show nothing rather than "Unknown Location".
  const location =
    session.city && session.country
      ? `${session.city}, ${session.country}`
      : (session.country ?? '');

  const lastActive = formatSGT(session.last_activity, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const createdAt = formatSGT(session.created_at, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return {
    deviceName,
    location,
    lastActive,
    createdAt,
    os: session.os,
    browser: session.browser,
  };
}
